/**
 * Migrates data from fl_wishlist (old) to wishlist_handles (new).
 * Ensures no duplicates and safe cleanup.
 */
const migrateOldWishlist = () => {
  const OLD_KEY = 'fl_wishlist';
  const rawOldData = localStorage.getItem(OLD_KEY);

  if (!rawOldData) return;

  try {
    const oldWishlist = JSON.parse(rawOldData);
    if (!Array.isArray(oldWishlist) || oldWishlist.length === 0) {
      localStorage.removeItem(OLD_KEY); // Cleanup empty/malformed
      return;
    }

    let currentWishlist = WishlistStore.get();
    let migratedCount = 0;

    oldWishlist.forEach((oldItem) => {
      // We only migrate if the handle isn't already there
      const oldWishlistedHandle = oldItem.handle;
      const existingHandles = new Set(currentWishlist.map((i) => i.handle));
      if (oldWishlistedHandle && !existingHandles.has(oldWishlistedHandle)) {
        let masterSku = oldItem.id;
        if (oldItem.variants && oldItem.variants.length > 0) {
          const firstVariant = oldItem.variants[0];
          if (oldItem.sku) {
            const skuParts = oldItem.sku.split('-');
            const lastPart = skuParts[skuParts.length - 1];

            if (oldItem.variants.length === 1 && lastPart === 'ONE') {
              masterSku = firstVariant.sku;
            } else if (oldItem.variants.length > 1 && lastPart !== 'ONE') {
              masterSku = skuParts.slice(0, -1).join('-');
            } else {
              masterSku = firstVariant.sku;
            }
          }
        }

        currentWishlist.push({ handle: oldWishlistedHandle, sku: masterSku });
        migratedCount++;
      }
    });

    if (migratedCount > 0) {
      // Save merged list and trigger UI update
      WishlistStore.set(currentWishlist);
    }

    // Crucial: Remove old key so we don't migrate again on next reload
    localStorage.removeItem(OLD_KEY);
  } catch (e) {
    // console.error('Wishlist migration failed:', e);
    // We don't remove the old key here to allow for manual recovery if it was a syntax error
  }
};

/**
 * Wishlist Engine - Reactive Implementation
 */
const LOCAL_STORAGE_KEY = 'wishlist_handles';

const WishlistStore = {
  isProcessing: false,
  get: () => {
    try {
      // Data is an array of objects: [{handle: 'abc', sku: 'xyz'}]
      return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || [];
    } catch (e) {
      console.warn('WishlistStore: localStorage read failed', e);
      return [];
    }
  },
  set: (wishlist, action, handle, btn) => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(wishlist));
      window.dispatchEvent(
        new CustomEvent('wishlist:updated', {
          detail: {
            wishlist,
            action,
            handle,
            btn,
          },
        }),
      );
      return true;
    } catch (e) {
      console.error('WishlistStore: localStorage write failed', e);
      return false;
    }
  },
  toggle: (handle, btn) => {
    if (WishlistStore.isProcessing) return;

    try {
      WishlistStore.isProcessing = true;
      if (btn) {
        btn.disabled = true;
      }

      const wishlist = WishlistStore.get();
      const index = wishlist.findIndex((item) => item.handle === handle);
      const exists = index !== -1;
      const action = exists ? 'remove' : 'add';

      if (exists) {
        wishlist.splice(index, 1);
      } else {
        const sku = btn?.closest('[data-master-sku]')?.dataset.masterSku || '';
        wishlist.push({ handle, sku });
      }
      WishlistStore.set(wishlist, action, handle, btn);
    } catch (e) {
      console.error('WishlistStore: toggle failed', e);
    } finally {
      WishlistStore.isProcessing = false;
      if (btn) {
        btn.disabled = false;
      }
    }
  },
};

/**
 * Updates button states within a specific container.
 * Useful for lazy-loaded products.
 */
const updateWishlistStateImmediately = (container = document, wishlist = WishlistStore.get()) => {
  const buttons = container.querySelectorAll('.js-wishlist-button');

  buttons.forEach((btn) => {
    const handle = btn.closest('[data-product-handle]')?.dataset.productHandle;
    if (handle) {
      const isAdded = wishlist.some((item) => item.handle === handle);
      btn.setAttribute('aria-pressed', isAdded ? 'true' : 'false');
      if (btn.dataset.wishlistAdd && btn.dataset.wishlistRemove) {
        btn.setAttribute('aria-label', isAdded ? btn.dataset.wishlistRemove : btn.dataset.wishlistAdd);
      }
      btn.classList.remove('hidden');
    }
  });
};

// Expose globally for lazy-load callbacks
window.updateWishlistStateImmediately = updateWishlistStateImmediately;

const setupWishlistListeners = () => {
  document.body.addEventListener('click', (event) => {
    const btn = event.target.closest('.js-wishlist-button');
    if (!btn) return;

    event.preventDefault();
    event.stopPropagation();

    const handle = btn.closest('[data-product-handle]')?.dataset.productHandle;

    if (handle) {
      WishlistStore.toggle(handle, btn);
    }
  });
  window.addEventListener('wishlist:updated', ({ detail }) => {
    const { wishlist, action, handle, btn } = detail;
    const size = wishlist.length;
    // Header Bubble
    const bubble = document.querySelector('.wishlist-count-bubble');
    if (bubble) {
      bubble.textContent = size;
      bubble.classList.toggle('visually-hidden', size === 0);
    }

    // Global hearts Sync
    if (handle) {
      const selector = `[data-product-handle="${handle}"] .js-wishlist-button`;
      document.querySelectorAll(selector).forEach((btn) => {
        const isAdded = action === 'add';
        btn.setAttribute('aria-pressed', isAdded ? 'true' : 'false');
        if (btn.dataset.wishlistAdd && btn.dataset.wishlistRemove) {
          btn.setAttribute('aria-label', isAdded ? btn.dataset.wishlistRemove : btn.dataset.wishlistAdd);
        }
      });
    }

    // Wishlist Page Specific Logic
    if (window.location.pathname.includes('/pages/wishlist')) {
      const infoBar = document.querySelector('.js-wishlist-info-bar');
      if (infoBar) {
        infoBar.dataset.wishlistCount = size;
        const countLabel = infoBar.querySelector('.count');
        if (countLabel) countLabel.textContent = size;
      }

      if (action === 'remove' && handle) {
        const card = document.querySelector(`[data-product-handle="${handle}"]`)?.closest('.grid__item');
        if (card) card.remove(); // Direct removal
      }
      if (action === 'add' && handle) {
        requestAnimationFrame(() => {
          renderWishlistPage([...wishlist]);
        });
      }
    }
  });
};

const renderWishlistPage = (wishlist) => {
  const container = document.querySelector('#wishlist-main-product-list');
  if (!container) return;
  const handles = wishlist.map((item) => item.handle);
  if (handles.length === 0) {
    container.innerHTML = '';
    return;
  }
  const skeletonQty = Math.min(4, handles.length);
  container.innerHTML = `
    <lazy-load-products
      data-collection="all"
      data-list-item-class="grid__item product-grid grid--3-col grid--3-col-desktop"
      data-handles="${handles.join('+')}"
    >
      <ul class="grid js-products-container">
        ${Array(skeletonQty)
          .fill(
            `
<skeleton-card-product class="grid__item product-grid grid--3-col grid--3-col-desktop ">
  <div class="image-placeholder"></div>
  <div class="title-placeholder"></div>
  <div class="price-placeholder"></div>
  <div class="cta-placeholder large-up-hide"></div>
</skeleton-card-product>
            `,
          )
          .join('')}
      </ul>
    </lazy-load-products>
  `;
};

const initWishlistApp = () => {
  setupWishlistListeners();
  // Initial Sync
  const initialWishlist = WishlistStore.get();
  WishlistStore.set(initialWishlist);
  migrateOldWishlist();
  updateWishlistStateImmediately();
  // If we are on the wishlist page, render immediately
  if (window.location.pathname.includes('/pages/wishlist')) {
    renderWishlistPage([...initialWishlist]);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlistApp);
} else {
  initWishlistApp();
}
